import { createElement, SlidersHorizontal } from "lucide";
import Clarity from "@microsoft/clarity";
import * as CookieConsent from "vanilla-cookieconsent";
export const ANALYTICS_KEY = "weekkeep.cookie-preferences.v2";
export const CLARITY_PROJECT = "yes20noabt";
const names = new Set([
  "memory_saved",
  "compass_saved",
  "journal_saved",
  "view_life",
  "view_today",
  "view_compass",
  "view_remember",
  "page_daily",
  "page_archive",
]);
export function analyticsController(win, sdk, gaID = "") {
  const eligible =
    win.location.origin === "https://weekkeep.com" &&
    ["/", "/app", "/app/"].includes(win.location.pathname) &&
    !win.location.search &&
    !win.location.hash;
  const validGA = /^G-[A-Z0-9]+$/.test(gaID);
  let clarityStarted = false,
    clarityStopped = false,
    clarityAllowed = false,
    googleStarted = false,
    googleAllowed = false;
  function gtag() {
    win.dataLayer ||= [];
    win.dataLayer.push(arguments);
  }
  const googleConsent = (granted) => ({
    analytics_storage: granted ? "granted" : "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  function apply({ clarity = false, google = false } = {}) {
    clarityAllowed = eligible && clarity;
    googleAllowed = eligible && validGA && google;
    if (clarityAllowed && !clarityStarted) {
      win.clarity ||= function () {
        (win.clarity.q ||= []).push(arguments);
      };
      sdk.consentV2({ analytics_Storage: "granted", ad_Storage: "denied" });
      sdk.init(CLARITY_PROJECT);
      clarityStarted = true;
      sdk.setTag(
        "surface",
        win.location.pathname === "/" ? "landing" : "journal",
      );
    } else if (!clarityAllowed && clarityStarted && !clarityStopped) {
      sdk.consentV2({ analytics_Storage: "denied", ad_Storage: "denied" });
      sdk.consent(false);
      win.clarity("stop");
      clarityStopped = true;
    }
    if (validGA) {
      win["ga-disable-" + gaID] = !googleAllowed;
      if (googleAllowed && !googleStarted) {
        gtag("consent", "default", googleConsent(false));
        gtag("consent", "update", googleConsent(true));
        gtag("js", new Date());
        gtag("config", gaID, {
          send_page_view: false,
          allow_google_signals: false,
          allow_ad_personalization_signals: false,
          page_location: win.location.origin + win.location.pathname,
          page_referrer: "",
          page_title: "Weekkeep",
        });
        const script = win.document.createElement("script");
        script.async = true;
        script.src = "https://www.googletagmanager.com/gtag/js?id=" + gaID;
        script.id = "weekkeep-google-tag";
        win.document.head.append(script);
        googleStarted = true;
        gtag("event", "page_view", {
          send_to: gaID,
          page_location: win.location.origin + win.location.pathname,
          page_title: "Weekkeep",
          page_referrer: "",
        });
      } else if (googleStarted)
        gtag("consent", "update", googleConsent(googleAllowed));
    }
  }
  return {
    apply,
    event(name) {
      if (!names.has(name)) return;
      if (clarityAllowed && !clarityStopped) sdk.event(name);
      if (googleAllowed && googleStarted)
        gtag("event", name, { send_to: gaID });
    },
  };
}
export function consentConfig({ gaID = "", update = () => {} } = {}) {
  const google = /^G-[A-Z0-9]+$/.test(gaID);
  return {
    revision: google ? 3 : 2,
    mode: "opt-in",
    autoShow: true,
    hideFromBots: false,
    cookie: {
      name: "weekkeep_consent",
      expiresAfterDays: 180,
      sameSite: "Lax",
      path: "/",
    },
    guiOptions: {
      consentModal: {
        layout: "box wide",
        position: "bottom left",
        equalWeightButtons: true,
      },
      preferencesModal: { layout: "box", equalWeightButtons: true },
    },
    onConsent: update,
    onChange: update,
    categories: {
      necessary: { enabled: true, readOnly: true },
      analytics: {
        enabled: false,
        services: {
          clarity: {
            label: "Microsoft Clarity · masked session recordings",
            cookies: [{ name: /^_cl(ck|sk)$/ }],
          },
          ...(google
            ? {
                google: {
                  label: "Google Analytics · usage statistics",
                  cookies: [{ name: /^_ga/ }],
                },
              }
            : {}),
        },
      },
    },
    language: {
      default: "en",
      translations: {
        en: {
          consentModal: {
            title: "Your journal. Your cookie choices.",
            description:
              "We use essential storage to keep your journal secure and remember your preferences. Optional analytics helps improve Weekkeep. It stays off unless you choose to allow it.",
            acceptAllBtn: "Accept all optional",
            acceptNecessaryBtn: "Reject optional",
            showPreferencesBtn: "Manage preferences",
          },
          preferencesModal: {
            title: "Cookie preferences",
            acceptAllBtn: "Accept all optional",
            acceptNecessaryBtn: "Reject optional",
            savePreferencesBtn: "Save my choices",
            closeIconLabel: "Close preferences",
            sections: [
              {
                title: "Choose what you share",
                description:
                  "Change or withdraw consent at any time using Cookie preferences. Choices last 180 days on this browser. Advertising is disabled. Re-enabling Clarity after withdrawal takes effect on your next page visit.",
              },
              {
                title: "Essential storage · always active",
                linkedCategory: "necessary",
                description:
                  'Weekkeep uses secure session cookies for sign-in, local storage for your journal and drafts, and a consent cookie for these choices. Cloudflare delivers and protects the site; security cookies may be used when needed. These functions cannot be switched off here. <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer">Cloudflare privacy</a>.',
              },
              {
                title: "Optional analytics",
                linkedCategory: "analytics",
                description:
                  'Microsoft Clarity records interactions and layouts with journal text and images masked. Usage and device information goes to Microsoft. We never add writing, recovery words or account IDs to analytics events. <a href="https://privacy.microsoft.com/privacystatement" target="_blank" rel="noopener noreferrer">Microsoft privacy</a>.' +
                  (google
                    ? ' Google Analytics measures page visits and fixed usage events. It loads only after your consent; advertising features remain disabled. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google privacy</a>.'
                    : " Google Analytics is not currently configured and does not load."),
              },
              {
                title: "Cloudflare analytics and tag injection",
                description:
                  "Cloudflare's optional Web Analytics beacon and Zaraz tag injection are not enabled on this site. Cloudflare delivery and security are separate from optional visitor analytics.",
              },
            ],
          },
        },
      },
    },
  };
}
export async function startAnalytics({
  win = window,
  sdk = Clarity,
  cmp = CookieConsent,
} = {}) {
  if (
    win.location.origin !== "https://weekkeep.com" ||
    !["/", "/app", "/app/"].includes(win.location.pathname)
  )
    return;
  const gaID =
    win.document.querySelector('meta[name="weekkeep-ga-id"]')?.content || "";
  const controller = analyticsController(win, sdk, gaID);
  const update = () => {
    controller.apply({
      clarity: cmp.acceptedService("clarity", "analytics"),
      google: cmp.acceptedService("google", "analytics"),
    });
    try {
      win.localStorage.setItem(
        ANALYTICS_KEY,
        JSON.stringify({ at: Date.now() }),
      );
    } catch {}
  };
  // The former Clarity-only choice must never authorize a newly added service.
  try {
    win.localStorage.removeItem("weekkeep.analytics-consent.v1");
  } catch {}
  const button = win.document.createElement("button");
  button.type = "button";
  button.className = "analytics-preferences";
  const label = win.document.createElement("span");
  label.textContent = "Cookie preferences";
  button.append(
    createElement(SlidersHorizontal, {
      width: 16,
      height: 16,
      "aria-hidden": "true",
      focusable: "false",
    }),
    label,
  );
  button.onclick = () => cmp.showPreferences();
  win.document.body.append(button);
  win.addEventListener("weekkeep:insight", (e) => controller.event(e.detail));
  win.document.addEventListener("click", (e) => {
    const b = e.target.closest("[data-action]");
    if (!b) return;
    if (b.dataset.action === "section")
      controller.event("view_" + (b.dataset.section || "").toLowerCase());
    if (b.dataset.action === "page")
      controller.event("page_" + b.dataset.scope);
  });
  const refresh = () => {
    const cookie = cmp.getCookie();
    const valid =
      cookie.revision === (/^G-[A-Z0-9]+$/.test(gaID) ? 3 : 2) &&
      Date.parse(cookie.lastConsentTimestamp || cookie.consentTimestamp) >
        Date.now() - 180 * 86400000;
    controller.apply({
      clarity: valid && cookie.services?.analytics?.includes("clarity"),
      google: valid && cookie.services?.analytics?.includes("google"),
    });
  };
  win.addEventListener("storage", (e) => {
    if (e.key === ANALYTICS_KEY || e.key === null) refresh();
  });
  win.addEventListener("focus", refresh);
  await cmp.run(consentConfig({ gaID, update }));
  return controller;
}
